import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    OnDestroy,
    Output,
    OnChanges,
    SimpleChanges,
    Inject,
} from '@angular/core';
import { isEmpty, cloneDeep, unset } from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { IpsecTunnel } from '@netfoundry-ui/shared/model';
import { ApiService, LoggerService, IpsecTunnelService, AgentService, ValidateService } from '@netfoundry-ui/shared/services';
import { Subscription, interval } from 'rxjs';
import { GrowlerService, GrowlerData } from '@netfoundry-ui/shared/growler';
import { RxTxSample, DowntimeRange, ChartMarker } from '@netfoundry-ui/ui/rx-tx-timeline-chart';

@Component({
    selector: 'app-ipsec-tunnel-form',
    templateUrl: './ipsec-tunnel-form.component.html',
    styleUrls: ['./ipsec-tunnel-form.component.scss'],
    standalone: false
})
export class IpsecTunnelFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isModal = false;
    @Input() formData: any = {};
    close: EventEmitter<any> = new EventEmitter<any>();

    isEditing = false;
    errMap: any = {};
    moreActions: any[] = [];
    protected isLoading = false;
    initData: any = {};
    @Output() dataChange: EventEmitter<any> = new EventEmitter<any>();
    _dataChange = false;
    basePath = '';
    previousRoute;
    protected usePreviousLocation = true;
    subscription: Subscription = new Subscription();
    @Input() entityId: string;
    baseHref: any;
    formInit = false;
    frontdoorId: string;
    processing = false;
    metricSamples: RxTxSample[] = [];
    // CPU and memory share the rx/tx chart: two series over the same buckets, percent instead of
    // bytes. Kept separate from metricSamples because the units and the y-axis differ.
    systemSamples: RxTxSample[] = [];
    livenessSamples: RxTxSample[] = [];
    metricsLoading = false;
    metricsWindow = '1h';
    uptimePercent: number | null = null;
    downtimeRanges: DowntimeRange[] = [];
    rekeyMarkers: ChartMarker[] = [];

    // View mode splits into tabs; create mode stays a single form.
    activeTab: 'overview' | 'ipsec' | 'metrics' = 'overview';

    // Committed CIDRs, rendered as pills. Mirrors the network tunneler form so the two IPsec
    // forms behave identically.
    peerCidrs: string[] = [];
    // Text not yet committed to a pill. Flushed on submit, so a customer who types one CIDR and
    // presses Create never has to discover Enter or the + button.
    peerCidrInput = '';

    // Generating is the default and the norm; a supplied key exists for customers whose device is
    // already configured, or whose process requires the key to originate on their side.
    // Fields with somewhere to render a message; anything else from the server becomes a growler.
    private readonly knownFields = ['name', 'peerPublicIp', 'peerCidr', 'peerCidrs', 'psk', 'region', 'size'];

    /**
     * Local error keys that mean the same field as a server violation path. The two sets grew
     * separately — local checks use flags like `nameRequired`, the server reports `$.name` — so a
     * field is highlighted if either has something to say about it.
     */
    private readonly errorAliases: Record<string, string[]> = {
        name: ['nameRequired'],
        peerPublicIp: ['peerPublicIpRequired', 'peerPublicIpInvalid'],
        peerCidrs: [],
        psk: [],
    };
    pskMode: 'generate' | 'provide' = 'generate';
    pskInput = '';
    pskRevealed = false;

    // Bridge health — what the bridge is doing now, as distinct from formData.status, which records
    // how far provisioning got. A tunnel can be DEPLOYED while its bridge is unreachable.
    bridgeHealth: any = null;
    bridgeHealthLoading = false;

    // Bridge logs. Sources are the IPsec daemon and the agent that hosts the share listeners; a
    // zrok bridge runs no ziti router, so charon and frontdoor-agent are the only two kinds.
    readonly eventKinds = ['charon', 'frontdoor-agent'];
    events: any[] = [];
    eventsLoading = false;
    eventsWindow = '1h';
    eventKindFilter: string | null = null;
    // Committed search term. Kept separate from the input below so typing does not fire a query
    // per keystroke; the term is applied on Enter or via the search button.
    eventSearch: string | null = null;
    eventSearchInput = '';
    eventsPageIndex = 0;
    readonly eventsPageSize = 20;

    eventsRefreshSeconds = 0;
    private eventsRefresh?: Subscription;
    metricsRefreshSeconds = 0;
    private metricsRefresh?: Subscription;
    private eventsLoaded = false;

    constructor(
        private apiService: ApiService,
        public agentService: AgentService,
        private logger: LoggerService,
        public svc: IpsecTunnelService,
        private growlerService: GrowlerService,
        private validateService: ValidateService,
        private location: Location,
        protected router?: Router,
        protected route?: ActivatedRoute,
    ) {
        this.previousRoute = router?.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString();
        this.subscription.add(
            this.route?.params?.subscribe(params => {
                const id = params['id'];
                if (isEmpty(id)) {
                    this.entityId = 'create';
                } else {
                    this.entityId = id;
                }
            })
        );
        this.subscription.add(
            router?.events.subscribe((event: any) => {
                if (!event?.snapshot?.routeConfig?.path) {
                    return;
                }
                const pathSegments = event.snapshot.routeConfig.path.split('/');
                this.basePath = pathSegments[0];
            })
        );
        this.initBaseHref();
    }

    ngOnInit(): void {
        this.subscription.add(
            this.apiService.currentTenant.subscribe((tenant) => {
                this.agentService.getAccountById(tenant.id).subscribe((frontdoor: any) => {
                    this.frontdoorId = frontdoor.id;
                    if (this.entityId === 'create') {
                        this.formData = new IpsecTunnel();
                        this.initData = cloneDeep(this.formData);
                        this.entityUpdated();
                        return;
                    } else {
                        this.isEditing = true;
                        this.isLoading = true;
                        this.getIpsecTunnel();
                    }
                });
            })
        );
        this.formInit = true;
    }

    getIpsecTunnel() {
        return this.svc.getIpsecTunnel(this.frontdoorId, this.entityId).subscribe({
            next: (entity: any) => {
                this.formData = entity;
                this.initData = cloneDeep(this.formData);
                this.entityUpdated();
                this.isLoading = false;
                this.loadMetrics();
                this.loadBridgeHealth();
            },
            error: (err) => {
                this.growlerService.show(
                    new GrowlerData(
                        'error',
                        'Error',
                        'Error retrieving IPsec tunnel data.',
                    )
                );
                this.isLoading = false;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this.isEditing = !isEmpty(this.formData.id);
    }

    initBaseHref() {
        try {
            const base = document?.querySelector('base');
            this.baseHref = base ? base.getAttribute('href') : '/';
        } catch (error) {
            this.baseHref = '/';
        }
    }

    entityUpdated() {
        unset(this.formData, '_links');
        this.initData = cloneDeep(this.formData);
    }

    headerActionRequested(action: any) {
        switch (action.name) {
            case 'save':
                this.save();
                break;
            case 'close':
                this.closeForm();
                break;
        }
    }

    save(event?: any) {
        if (this.isEditing) { return; }
        if (!this.validate()) {
            return;
        }

        this.processing = true;
        this.isLoading = true;

        const model = this.buildModel();

        // The server runs the same constraints the create would, and reports every violation
        // rather than failing on the first. Asking first means a rejected request never reaches
        // the create path, where a partial failure would have to be cleaned up.
        this.svc.validateCreate(model, this.frontdoorId).subscribe({
            next: (violations: { path: string; message: string }[]) => {
                if (violations?.length) {
                    this.applyServerViolations(violations);
                    this.processing = false;
                    this.isLoading = false;
                    return;
                }
                this.submitCreate(model);
            },
            // A validation call that cannot be reached should not block the attempt: create
            // enforces the same rules, so fall through and let it answer.
            error: () => this.submitCreate(model),
        });
    }

    private buildModel() {
        return {
            name: this.formData.name,
            peerPublicIp: this.formData.peerPublicIp,
            // Sent as peerCidrs only. The API also accepts the deprecated single peerCidr, but
            // rejects a request carrying both, so this must not send the old field as well.
            peerCidrs: this.peerCidrs,
            // Omitted entirely when generating: the API treats absent as "generate one", and
            // sending an empty string would be a supplied key that fails validation.
            ...(this.pskMode === 'provide' ? { psk: this.pskInput } : {}),
        };
    }

    /**
     * Maps violations onto the fields that render them. Paths arrive as `$.peerCidrs`; anything
     * this form has no slot for is surfaced as a growler rather than dropped, so a new server-side
     * constraint cannot fail silently.
     */
    private applyServerViolations(violations: { path: string; message: string }[]) {
        const unmapped: string[] = [];
        violations.forEach((violation) => {
            const field = (violation.path ?? '').replace(/^\$\./, '');
            if (field && Object.prototype.hasOwnProperty.call(this.errMap, field) || this.knownFields.includes(field)) {
                this.errMap[field] = violation.message;
            } else {
                unmapped.push(`${field || 'request'}: ${violation.message}`);
            }
        });
        if (unmapped.length) {
            this.growlerService.show(
                new GrowlerData('error', 'Error', 'IPsec tunnel is not valid', unmapped.join('; '))
            );
        }
    }

    private submitCreate(model: any) {
        this.svc.create(model, this.frontdoorId).subscribe({
            next: (result: any) => {
                this.processing = false;
                if (this.isModal) {
                    this.closeModal(true, true);
                } else {
                    this.growlerService.show(
                        new GrowlerData(
                            'success',
                            'Success',
                            'IPsec tunnel submitted',
                            'The tunnel is being provisioned. It will appear as Deployed once complete.'
                        )
                    );
                    this.returnToListPage();
                }
            },
            error: (err) => {
                this.processErrors(err);
                this.isLoading = false;
                this.processing = false;
            },
        });
    }

    validate() {
        this.errMap = {};

        if (isEmpty(this.formData.name)) {
            this.errMap['nameRequired'] = true;
        }

        if (isEmpty(this.formData.peerPublicIp)) {
            this.errMap['peerPublicIpRequired'] = true;
        } else if (!this.validateService.isValidIP(this.formData.peerPublicIp)) {
            this.errMap['peerPublicIpInvalid'] = true;
        }

        // Flush text still sitting in the input into a pill, so submitting without pressing Enter
        // or + does what the user plainly meant. Invalid text is left in place and reported below,
        // where the message naming the bad value beats a generic "required".
        const pending = this.peerCidrInput?.trim();
        if (pending && this.validateService.isValidCidrIP(pending)) {
            if (!this.peerCidrs.includes(pending)) {
                this.peerCidrs = [...this.peerCidrs, pending];
            }
            this.peerCidrInput = '';
        }

        if (this.peerCidrs.length === 0) {
            this.errMap['peerCidrs'] = 'At least one peer CIDR is required';
        } else {
            const bad = this.peerCidrs.find((cidr) => !this.validateService.isValidCidrIP(cidr));
            if (bad) {
                this.errMap['peerCidrs'] = `${bad} is not a valid CIDR`;
            }
        }
        if (this.peerCidrInput?.trim()) {
            this.errMap['peerCidrs'] = `${this.peerCidrInput.trim()} is not a valid CIDR`;
        }

        const pskError = this.pskError();
        if (pskError) {
            this.errMap['psk'] = pskError;
        }

        return isEmpty(this.errMap);
    }

    /** True when this field has an error from either source, so the control can be marked. */
    hasError(field: string): boolean {
        return !!this.errMap[field] || (this.errorAliases[field] ?? []).some((alias) => !!this.errMap[alias]);
    }

    /**
     * The message to show beneath a field, when there is one worth showing. Local checks store
     * booleans and the template supplies its own wording for those; only a server violation
     * carries text, so a non-string value yields null rather than "true".
     */
    errorText(field: string): string | null {
        const value = this.errMap[field];
        return typeof value === 'string' && value.length > 0 ? value : null;
    }

    // Element ids of the create form's inputs, in the order Enter walks them. The pre-shared key
    // is only in the list while it is on screen, so Enter on the CIDR box does not aim at a field
    // that is not rendered.
    private static readonly FIELD_NAME = 'IpsecTunnelName';
    private static readonly FIELD_PEER_PUBLIC_IP = 'IpsecTunnelPeerPublicIp';
    private static readonly FIELD_PEER_CIDRS = 'IpsecTunnelPeerCidrInput';
    private static readonly FIELD_PSK = 'IpsecTunnelPsk';
    private static readonly FIELD_PSK_MODE_GENERATE = 'IpsecTunnelPskModeGenerate';
    private static readonly FIELD_PSK_MODE_PROVIDE = 'IpsecTunnelPskModeProvide';

    /**
     * The toggle's focusable element is whichever option is selected — a roving tabindex, as a
     * radio group has — so its place in the field order is a moving target.
     */
    private pskModeFieldId(): string {
        return this.pskMode === 'generate'
            ? IpsecTunnelFormComponent.FIELD_PSK_MODE_GENERATE
            : IpsecTunnelFormComponent.FIELD_PSK_MODE_PROVIDE;
    }

    private fieldOrder(): string[] {
        const order = [
            IpsecTunnelFormComponent.FIELD_NAME,
            IpsecTunnelFormComponent.FIELD_PEER_PUBLIC_IP,
            IpsecTunnelFormComponent.FIELD_PEER_CIDRS,
            this.pskModeFieldId(),
        ];
        if (this.pskMode === 'provide') {
            order.push(IpsecTunnelFormComponent.FIELD_PSK);
        }
        return order;
    }

    /** The field Enter should move to, or null at the end of the form. */
    nextFieldId(current: string): string | null {
        const order = this.fieldOrder();
        const index = order.indexOf(current);
        return index >= 0 && index < order.length - 1 ? order[index + 1] : null;
    }

    /**
     * Enter advances rather than submitting. Creating a tunnel provisions a VM, so it should take
     * a deliberate click on Create — not a keystroke someone used to leave a field.
     *
     * <p>Nothing to commit here beyond moving on: the inputs are bound with ngModel, so the value
     * is already on the model by the time Enter arrives.
     */
    onFieldEnter(current: string) {
        const next = this.nextFieldId(current);
        if (next) {
            this.focusField(next);
        }
    }

    private focusField(id: string) {
        if (typeof document !== 'undefined') {
            document.getElementById(id)?.focus();
        }
    }

    /**
     * Enter in the CIDR box commits what has been typed, so several ranges can be entered without
     * reaching for the mouse. An empty box means there is nothing left to add, so it advances
     * instead.
     */
    onPeerCidrEnter() {
        if ((this.peerCidrInput ?? '').trim().length > 0) {
            this.addCidr(this.peerCidrInput);
            return;
        }
        this.onFieldEnter(IpsecTunnelFormComponent.FIELD_PEER_CIDRS);
    }

    /**
     * Arrow keys move between the toggle's options the way they do in a radio group, selecting as
     * they go, and focus follows so the next Enter continues from the right place. Space and Enter
     * on a focused option are handled by the buttons themselves.
     */
    movePskMode(direction: -1 | 1) {
        const modes: ('generate' | 'provide')[] = ['generate', 'provide'];
        const next = modes[(modes.indexOf(this.pskMode) + direction + modes.length) % modes.length];
        this.setPskMode(next);
        this.focusField(this.pskModeFieldId());
    }

    setPskMode(mode: 'generate' | 'provide') {
        this.pskMode = mode;
        if (mode === 'generate') {
            // Dropped rather than kept hidden, so switching back cannot submit a key the reader
            // believes they discarded.
            this.pskInput = '';
            this.pskRevealed = false;
        }
        delete this.errMap['psk'];
    }

    togglePskReveal() {
        this.pskRevealed = !this.pskRevealed;
    }

    /**
     * Only the one rule the server cannot apply: it reads an absent key as "generate one", so it
     * has no way to know the reader chose to supply a key and then left the box empty. Every other
     * constraint — length, quotes, backslashes, Jinja delimiters, whitespace — is reported by
     * validateCreate, so the rules live in ValidIpsecPsk alone rather than in two places that have
     * to be kept in step.
     */
    pskError(): string | null {
        if (this.pskMode !== 'provide') {
            return null;
        }
        return (this.pskInput ?? '').length === 0 ? 'A pre-shared key is required' : null;
    }

    addCidr(value: string) {
        const cidr = (value || '').trim();
        if (!cidr) {
            this.errMap['peerCidrs'] = 'A peer CIDR is required';
            return;
        }
        if (!this.validateService.isValidCidrIP(cidr)) {
            this.errMap['peerCidrs'] = `${cidr} is not a valid CIDR`;
            return;
        }
        // Re-adding one already listed is a no-op rather than an error: the intent is unambiguous
        // and the list already reflects it.
        if (!this.peerCidrs.includes(cidr)) {
            this.peerCidrs = [...this.peerCidrs, cidr];
        }
        this.peerCidrInput = '';
        delete this.errMap['peerCidrs'];
    }

    removeCidr(value: string) {
        this.peerCidrs = this.peerCidrs.filter((cidr) => cidr !== value);
    }

    loadMetrics() {
        this.metricsLoading = true;
        this.svc.getMetrics(this.frontdoorId, this.entityId, this.metricsWindow).subscribe({
            next: (data: any) => {
                this.metricSamples = this.transformMetrics(data);
                this.systemSamples = this.transformSystemMetrics(data);
                this.livenessSamples = this.transformLivenessMetrics(data);
                this.downtimeRanges = data?.downtimeRanges ?? [];
                this.rekeyMarkers = this.transformRekeyEvents(data);
                this.uptimePercent = this.calculateUptime(data);
                this.metricsLoading = false;
            },
            error: () => {
                this.metricSamples = [];
                this.systemSamples = [];
                this.livenessSamples = [];
                this.uptimePercent = null;
                this.downtimeRanges = [];
                this.rekeyMarkers = [];
                this.metricsLoading = false;
            },
        });
    }

    onWindowChange(window: string) {
        this.metricsWindow = window;
        this.loadMetrics();
    }

    setTab(tab: 'overview' | 'ipsec' | 'metrics') {
        this.activeTab = tab;
        // Each panel only polls while its own tab is on screen; polling elsewhere fetches what
        // nothing displays. The chosen intervals are remembered and resume on return.
        if (tab !== 'ipsec') {
            this.eventsRefresh?.unsubscribe();
            this.eventsRefresh = undefined;
        } else if (this.eventsRefreshSeconds > 0 && !this.eventsRefresh) {
            this.setEventsRefresh(this.eventsRefreshSeconds);
        }
        if (tab !== 'metrics') {
            this.metricsRefresh?.unsubscribe();
            this.metricsRefresh = undefined;
        } else if (this.metricsRefreshSeconds > 0 && !this.metricsRefresh) {
            this.setMetricsRefresh(this.metricsRefreshSeconds);
        }
        // A log fetch is 500 lines and only the IPsec tab renders them, so it is deferred until
        // something will actually show it. Left unset on failure so returning to the tab retries.
        if (tab === 'ipsec' && !this.eventsLoaded) {
            this.loadEvents();
        }
    }

    loadBridgeHealth() {
        this.bridgeHealthLoading = true;
        this.svc.getBridgeHealth(this.frontdoorId, this.entityId).subscribe({
            next: (data: any) => {
                this.bridgeHealth = data;
                this.bridgeHealthLoading = false;
            },
            error: () => {
                this.bridgeHealth = null;
                this.bridgeHealthLoading = false;
            },
        });
    }

    loadEvents() {
        this.eventsLoading = true;
        this.svc.getEvents(this.frontdoorId, this.entityId, this.eventsWindow, this.eventKindFilter, this.eventSearch).subscribe({
            next: (data: any) => {
                // A CollectionModel response carries the rows under _embedded; the key follows the
                // element type, so it is read positionally rather than by name.
                this.events = Array.isArray(data)
                    ? data
                    : (Object.values(data?._embedded ?? {})[0] as any[]) ?? [];
                this.eventsPageIndex = 0;
                this.eventsLoaded = true;
                this.eventsLoading = false;
            },
            error: () => {
                this.events = [];
                this.eventsPageIndex = 0;
                this.eventsLoading = false;
            },
        });
    }

    setEventsWindow(window: string) {
        this.eventsWindow = window;
        this.loadEvents();
    }

    setEventKindFilter(kind: string | null) {
        this.eventKindFilter = kind;
        this.loadEvents();
    }

    applyEventSearch() {
        const term = this.eventSearchInput.trim();
        this.eventSearch = term.length > 0 ? term : null;
        this.loadEvents();
    }

    clearEventSearch() {
        this.eventSearchInput = '';
        this.eventSearch = null;
        this.loadEvents();
    }

    /** Explicit refresh. Ignored while a fetch is already in flight so double-clicks do not stack. */
    refreshEvents() {
        if (!this.eventsLoading) {
            this.loadEvents();
        }
    }

    setEventsRefresh(seconds: number) {
        this.eventsRefreshSeconds = seconds;
        this.eventsRefresh?.unsubscribe();
        this.eventsRefresh = this.autoRefresh(seconds, () => this.eventsLoading, () => this.loadEvents());
    }

    /** Explicit refresh of the charts. Ignored while a fetch is already in flight. */
    refreshMetrics() {
        if (!this.metricsLoading) {
            this.loadMetrics();
        }
    }

    setMetricsRefresh(seconds: number) {
        this.metricsRefreshSeconds = seconds;
        this.metricsRefresh?.unsubscribe();
        this.metricsRefresh = this.autoRefresh(seconds, () => this.metricsLoading, () => this.loadMetrics());
    }

    /**
     * An interval that skips a backgrounded tab and an outstanding fetch — the first is polling
     * nobody is looking at, the second queues work behind a slow response. Registered for teardown
     * as well as returned, so leaving the page always stops it.
     */
    private autoRefresh(seconds: number, isBusy: () => boolean, load: () => void): Subscription | undefined {
        if (seconds <= 0) {
            return undefined;
        }
        const sub = interval(seconds * 1000).subscribe(() => {
            if (typeof document !== 'undefined' && document.hidden) {
                return;
            }
            if (isBusy()) {
                return;
            }
            load();
        });
        this.subscription.add(sub);
        return sub;
    }

    get pagedEvents(): any[] {
        const start = this.eventsPageIndex * this.eventsPageSize;
        return this.events.slice(start, start + this.eventsPageSize);
    }

    get eventsTotalPages(): number {
        return Math.max(1, Math.ceil(this.events.length / this.eventsPageSize));
    }

    nextEventsPage() {
        if (this.eventsPageIndex < this.eventsTotalPages - 1) {
            this.eventsPageIndex++;
        }
    }

    prevEventsPage() {
        if (this.eventsPageIndex > 0) {
            this.eventsPageIndex--;
        }
    }

    hasChildSAs(): boolean {
        return (this.bridgeHealth?.childSAs?.length ?? 0) > 0;
    }

    /** True once the bridge has reported at all; the snapshot is meaningless before that. */
    get hasReported(): boolean {
        return !!this.bridgeHealth && this.bridgeHealth.compositeHealth !== 'PROVISIONING';
    }

    get isStale(): boolean {
        return this.bridgeHealth?.compositeHealth === 'STALE';
    }

    formatBytes(n: number | null | undefined): string {
        if (n == null) return '—';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
        if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MiB`;
        return `${(n / 1024 / 1024 / 1024).toFixed(2)} GiB`;
    }

    formatPct(n: number | null | undefined): string {
        return n == null ? '—' : `${n.toFixed(1)}%`;
    }

    formatEpochMs(t: number | null | undefined): string {
        return t == null ? '—' : new Date(t).toLocaleString();
    }

    private transformMetrics(data: any): RxTxSample[] {
        const points: any[] = data?.series?.rxTx ?? [];
        // rx/tx are passed through as-is, including nulls. A null bucket is a counter reset at SA
        // rekey or a missed scrape, and the chart renders it as a gap; coercing it to 0 would draw
        // a floor and read as "no traffic" during a window where traffic was flowing.
        return points.map((p) => ({ timestamp: p.t, rx: p.rx, tx: p.tx }));
    }

    /**
     * CPU and memory per bucket. The endpoint already reports percentages, and it omits buckets
     * with no system sample rather than emitting nulls — metricbeat's system module scrapes on its
     * own cadence, so with a finer bucket interval every other bucket would otherwise be empty and
     * the line would have no two adjacent points to join.
     */
    private transformSystemMetrics(data: any): RxTxSample[] {
        const points: any[] = data?.series?.system ?? [];
        return points.map((p) => ({ timestamp: p.t, rx: p.cpuPct, tx: p.memPct }));
    }

    /**
     * Seconds since each direction last carried traffic, as two series over the same buckets. The
     * endpoint emits one entry per bucket to both arrays, so they align by index.
     *
     * Nulls pass straight through: a null means no SA in that bucket has moved a packet, and the
     * chart draws a gap. Substituting 0 would read as "traffic just now" and mask an outage —
     * which is exactly what the endpoint's bytes-greater-than-zero filter exists to prevent.
     */
    private transformLivenessMetrics(data: any): RxTxSample[] {
        const inbound: any[] = data?.series?.lastInboundSeconds ?? [];
        const outbound: any[] = data?.series?.lastOutboundSeconds ?? [];
        return inbound.map((p, i) => ({ timestamp: p.t, rx: p.v, tx: outbound[i]?.v ?? null }));
    }

    // Downtime ranges and rekey events are derived server-side so every consumer interprets a gap
    // the same way; the component only reshapes them for the chart.
    private transformRekeyEvents(data: any): ChartMarker[] {
        const events: any[] = data?.rekeyEvents ?? [];
        return events.map((e) => ({
            t: e.t,
            label: (e.childIds?.length ?? 0) > 1 ? 'IKE rebuild' : 'SA rekey',
        }));
    }

    /**
     * Availability across the window, from per-bucket IKE state. Buckets with no scrape are
     * excluded rather than counted as down — the bridge not reporting is a telemetry gap, not
     * evidence the tunnel was unavailable.
     */
    private calculateUptime(data: any): number | null {
        const state: any[] = data?.series?.tunnelState ?? [];
        const known = state.filter((p) => p?.ikeUp === true || p?.ikeUp === false);
        if (known.length === 0) {
            return null;
        }
        return (known.filter((p) => p.ikeUp === true).length / known.length) * 100;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    clear(): void {
    }

    closeModal(refresh = true, ignoreChanges = false, data?: any, event?: any): void {
        this.close.emit({ refresh: refresh });
        if (event) {
            event.stopPropagation();
        }
    }

    closeForm(refresh = true, ignoreChanges = false, data?, event?) {
        if (!ignoreChanges && this._dataChange) {
            const confirmed = confirm('You have unsaved changes. Do you want to leave this page and discard your changes or stay on this page?');
            if (!confirmed) {
                return;
            }
        }
        if (this.isModal) {
            this.closeModal(refresh, ignoreChanges);
        } else {
            this.returnToListPage();
        }
    }

    returnToListPage() {
        if (this.location && this.previousRoute && this.usePreviousLocation) {
            this.location.back();
        } else {
            // IPSec Tunnels is a tab on the IPSec shares page, not a page of its own.
            this.router?.navigateByUrl(`${this.basePath}/ipsec-shares?tab=tunnels`);
        }
    }

    private processErrors(errorResponse: any) {
        const errors: string[] = errorResponse?.error?.errors || [];
        const peerPublicIpError = errors.find(e => e.includes('peerPublicIp'));
        const peerCidrError = errors.find(e => e.includes('peerCidr'));
        const nameError = errors.find(e => e.includes('name'));

        if (peerPublicIpError) {
            this.errMap['peerPublicIpInvalid'] = true;
        }
        if (peerCidrError) {
            this.errMap['peerCidrInvalid'] = true;
        }
        if (nameError) {
            this.errMap['nameRequired'] = true;
        }

        this.growlerService.show(
            new GrowlerData('error', 'Error', 'IPsec tunnel request failed', errorResponse?.error?.detail || 'The request has failed.')
        );
    }

    /**
     * True when this form is showing an existing entity. Derived from the route/input
     * rather than the loaded record, so the header title doesn't briefly read
     * "Create New ..." while the record is still being fetched.
     */
    get isExistingEntity(): boolean {
        return !isEmpty(this.formData?.id) || (!isEmpty(this.entityId) && this.entityId !== 'create');
    }

}
